import type { ConversationCommonItem } from '@repo/common/types/Adapter';
import type { ExchangeMessage } from '@repo/common/types/Exchange';
import type { Task } from '@repo/common/types/Task';
import type { Transcript, TranscriptFull } from '@repo/common/types/Transcript';

/**
 * Deep equality comparison that ignores property order
 */
function deepEqual(obj1: any, obj2: any): boolean {
  // Handle primitive types and null/undefined
  if (obj1 === obj2) return true;
  // Treat null and undefined as equivalent for database compatibility
  if (obj1 == null && obj2 == null) return true;
  if (obj1 == null || obj2 == null) return false;

  // Handle Date objects - compare Date-to-Date or Date-to-string
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  // Handle Date-to-string comparison (for JSON round-trip)
  if (obj1 instanceof Date && typeof obj2 === 'string') {
    return obj1.toISOString() === obj2;
  }

  if (obj2 instanceof Date && typeof obj1 === 'string') {
    return obj2.toISOString() === obj1;
  }

  // If only one is a Date and the other is not a string, they're not equal
  if (obj1 instanceof Date || obj2 instanceof Date) {
    return false;
  }

  // Handle URL objects - compare URL-to-URL or URL-to-string
  if (obj1 instanceof URL && obj2 instanceof URL) {
    return obj1.toString() === obj2.toString();
  }

  // Handle URL-to-string comparison (for JSON round-trip)
  if (obj1 instanceof URL && typeof obj2 === 'string') {
    return obj1.toString() === obj2;
  }

  if (obj2 instanceof URL && typeof obj1 === 'string') {
    return obj2.toString() === obj1;
  }

  // If only one is a URL and the other is not a string, they're not equal
  if (obj1 instanceof URL || obj2 instanceof URL) {
    return false;
  }

  if (typeof obj1 !== typeof obj2) return false;

  // Handle arrays
  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2) || obj1.length !== obj2.length) return false;
    return obj1.every((item, index) => deepEqual(item, obj2[index]));
  }

  // Handle objects
  if (typeof obj1 === 'object') {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    const allKeysMatch = keys1.every((key) => keys2.includes(key));
    if (!allKeysMatch) {
      return false;
    }

    return keys1.every((key) => deepEqual(obj1[key], obj2[key]));
  }

  return false;
}

/**
 * Normalize sources field to treat [] and undefined as equivalent
 */
function normalizeSourcesField(value: any): any {
  // If value is an empty array, treat it as undefined for comparison purposes
  if (Array.isArray(value) && value.length === 0) {
    return undefined;
  }
  // If value is undefined or null, keep it as undefined
  if (value === null || value === undefined) {
    return undefined;
  }
  return value;
}

/**
 * Validation utilities for testing data integrity
 */
export class ValidationUtils {
  /**
   * Drop undefined-valued keys deeply from an object/array structure.
   */
  static dropUndefinedKeysDeep<T = any>(value: T): T {
    const prune = (val: any): any => {
      if (val === undefined) return undefined;
      if (val === null) return null;
      if (val instanceof Date) return val; // preserve special instances
      if (val instanceof URL) return val; // preserve special instances
      if (Array.isArray(val)) return val.map(prune);
      if (typeof val === 'object' && val !== null) {
        const out: Record<string, any> = {};
        for (const [k, v] of Object.entries(val)) {
          const pv = prune(v);
          if (pv !== undefined) out[k] = pv;
        }
        return out as any;
      }
      return val;
    };
    return prune(value);
  }

  /**
   * Drop null OR undefined-valued keys deeply from an object/array structure.
   * This is used for checksum normalization to treat null and undefined equivalently for key presence.
   */
  static dropNullishKeysDeep<T = any>(value: T): T {
    const prune = (val: any): any => {
      if (val === undefined) return undefined;
      if (val === null) return undefined; // treat null as undefined for hashing
      if (val instanceof Date) return val; // preserve special instances
      if (val instanceof URL) return val; // preserve special instances
      if (Array.isArray(val)) return val.map(prune);
      if (typeof val === 'object' && val !== null) {
        const out: Record<string, any> = {};
        for (const [k, v] of Object.entries(val)) {
          const pv = prune(v);
          if (pv !== undefined) out[k] = pv;
        }
        // Drop empty objects - treat them as undefined
        if (Object.keys(out).length === 0) return undefined;
        return out as any;
      }
      return val;
    };
    return prune(value);
  }
  /**
   * Create a uniform, comparable representation of an object for hashing/equality.
   * - Dates -> ISO strings
   * - Undefined -> null (to avoid omission differences)
   * - Keys sorted
   * - Optional field exclusions
   * - Sources field normalization: [] and undefined treated as equivalent
   */
  static toUniformComparable(value: any, options: { excludeFields?: string[] } = {}): any {
    const { excludeFields = [] } = options;

    const normalize = (val: any, key?: string): any => {
      if (val === undefined) return null;
      if (val === null) return null;
      if (val instanceof Date) return val.toISOString();
      if (val instanceof URL) return val.toString();

      // Special handling for sources field normalization
      if (key === 'sources') {
        const normalized = normalizeSourcesField(val);
        return normalized === undefined ? null : normalized;
      }

      // Special handling for duration field - floor to integer (Prisma truncates floats to Int)
      // Omit sub-second durations (< 1 second is meaningless)
      if (key === 'duration' && typeof val === 'number') {
        if (val < 1) return null;
        return Math.floor(val);
      }

      // Special handling for userNotes - normalize empty string to null
      if (key === 'userNotes' && val === '') {
        return null;
      }

      if (Array.isArray(val)) {
        return val.map((item) => normalize(item));
      }

      if (typeof val === 'object') {
        // Normalize empty objects to null for JSON fields like 'data'
        const objKeys = Object.keys(val);
        if (objKeys.length === 0 && (key === 'data' || key === 'output' || key === 'file')) {
          return null;
        }

        // Sort keys for stable representation
        const keys = objKeys.filter((k) => !excludeFields.includes(k)).sort();
        const out: Record<string, any> = {};
        for (const objKey of keys) {
          out[objKey] = normalize(val[objKey], objKey);
        }
        return out;
      }

      return val;
    };

    return normalize(value);
  }

  /**
   * Stable stringify for uniform comparable objects
   */
  static stableStringify(obj: any): string {
    // Assumes keys are already sorted by toUniformComparable
    return JSON.stringify(obj);
  }

  /**
   * Compute a SHA-256 checksum of a value after normalizing it.
   */
  static computeChecksum(value: any, options: { excludeFields?: string[] } = {}): string {
    // Use Web Crypto if available, else fallback to a simple hash (non-cryptographic) to avoid Node-only require.
    const dropped = ValidationUtils.dropNullishKeysDeep(value);
    const uniform = ValidationUtils.toUniformComparable(dropped, options);
    const payload = ValidationUtils.stableStringify(uniform);
    // FNV-1a 32-bit as a portable, fast checksum for equality comparisons
    let hash = 0x811c9dc5;
    for (let i = 0; i < payload.length; i++) {
      hash ^= payload.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    // Convert to unsigned and hex string
    return (hash >>> 0).toString(16).padStart(8, '0');
  }

  /**
   * Compute checksum for a common item using the full normalization pipeline.
   * Defaults to excluding only fields that cannot be reconciled across stores (legacy): pushedAt.
   * No timestamp exclusions by default to prevent drift.
   */
  static computeCommonChecksum(item: any, options: { excludeFields?: string[] } = {}): string {
    const defaultExclusions = ['pushedAt'];
    const excludeFields = Array.from(
      new Set([...(options.excludeFields || []), ...defaultExclusions])
    );

    // Apply sources normalization before other processing
    const normalizedItem = { ...item };
    if ('sources' in normalizedItem) {
      normalizedItem.sources = normalizeSourcesField(normalizedItem.sources);
    }

    // Full pipeline here to make this the single entrypoint for callers.
    const dropped = ValidationUtils.dropNullishKeysDeep(normalizedItem);
    const uniform = ValidationUtils.toUniformComparable(dropped, { excludeFields });
    const payload = ValidationUtils.stableStringify(uniform);

    // FNV-1a 32-bit hash
    let hash = 0x811c9dc5;
    for (let i = 0; i < payload.length; i++) {
      hash ^= payload.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
  }
  /**
   * Validate that two conversations are equivalent (ignoring timestamps if specified)
   */
  static validateConversationEquality(
    expected: ConversationCommonItem,
    actual: ConversationCommonItem,
    options: {
      ignoreTimestamps?: boolean;
      ignoreFields?: string[];
    } = {}
  ): { isEqual: boolean; differences: string[] } {
    const differences: string[] = [];
    const { ignoreTimestamps = false, ignoreFields = [] } = options;

    // Fields to compare
    const fieldsToCompare = [
      'id',
      'name',
      'editedName',
      'type',
      'transcriptId',
      'workspaceId',
      'userId',
      'data'
    ];

    if (!ignoreTimestamps) {
      fieldsToCompare.push('createdAt', 'updatedAt', 'deletedAt');
    }

    // Filter out ignored fields
    const finalFields = fieldsToCompare.filter((field) => !ignoreFields.includes(field));

    for (const field of finalFields) {
      const expectedValue = (expected as any)[field];
      const actualValue = (actual as any)[field];

      if (field === 'createdAt' || field === 'updatedAt' || field === 'deletedAt') {
        // Handle date comparison
        const expectedTime =
          expectedValue instanceof Date
            ? expectedValue.getTime()
            : expectedValue
              ? new Date(expectedValue).getTime()
              : null;
        const actualTime =
          actualValue instanceof Date
            ? actualValue.getTime()
            : actualValue
              ? new Date(actualValue).getTime()
              : null;

        if (expectedTime !== actualTime) {
          differences.push(`${field}: expected ${expectedValue}, got ${actualValue}`);
        }
      } else if (field === 'data') {
        // Handle JSON data comparison with deep equality to ignore property order
        if (!deepEqual(expectedValue, actualValue)) {
          differences.push(
            `${field}: expected ${JSON.stringify(expectedValue)}, got ${JSON.stringify(actualValue)}`
          );
        }
      } else {
        // Handle simple value comparison
        if (expectedValue !== actualValue) {
          differences.push(`${field}: expected ${expectedValue}, got ${actualValue}`);
        }
      }
    }

    return {
      isEqual: differences.length === 0,
      differences
    };
  }

  /**
   * Validate that all items in an array are unique by ID
   */
  static validateUniqueIds(items: ConversationCommonItem[]): {
    isUnique: boolean;
    duplicates: string[];
  } {
    const seen = new Set<string>();
    const duplicates: string[] = [];

    for (const item of items) {
      if (seen.has(item.id)) {
        duplicates.push(item.id);
      } else {
        seen.add(item.id);
      }
    }

    return {
      isUnique: duplicates.length === 0,
      duplicates
    };
  }

  /**
   * Validate that items are sorted by a specific field
   */
  static validateSorted(
    items: ConversationCommonItem[],
    field: keyof ConversationCommonItem,
    order: 'asc' | 'desc' = 'asc'
  ): { isSorted: boolean; violationIndex?: number } {
    for (let i = 1; i < items.length; i++) {
      const prev = items[i - 1];
      const curr = items[i];

      let prevValue = (prev as any)[field];
      let currValue = (curr as any)[field];

      // Handle date comparison
      if (prevValue instanceof Date) prevValue = prevValue.getTime();
      if (currValue instanceof Date) currValue = currValue.getTime();

      const isCorrectOrder = order === 'asc' ? prevValue <= currValue : prevValue >= currValue;

      if (!isCorrectOrder) {
        return {
          isSorted: false,
          violationIndex: i
        };
      }
    }

    return { isSorted: true };
  }

  /**
   * Validate that required fields are present and not null/undefined
   */
  static validateRequiredFields(
    item: ConversationCommonItem,
    requiredFields: (keyof ConversationCommonItem)[] = [
      'id',
      'name',
      'type',
      'userId',
      'createdAt',
      'updatedAt'
    ]
  ): { isValid: boolean; missingFields: string[] } {
    const missingFields: string[] = [];

    for (const field of requiredFields) {
      const value = (item as any)[field];
      if (value === null || value === undefined || value === '') {
        missingFields.push(field as string);
      }
    }

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  }

  /**
   * Validate that data can be round-tripped through JSON serialization
   */
  static validateJsonRoundTrip(item: ConversationCommonItem): { isValid: boolean; error?: string } {
    try {
      // Convert dates to strings (simulating database storage)
      const serialized = {
        ...item,
        createdAt: item.createdAt.toISOString(),
        updatedAt: item.updatedAt.toISOString(),
        deletedAt: item.deletedAt?.toISOString()
      };

      const jsonString = JSON.stringify(serialized);
      const parsed = JSON.parse(jsonString);

      // Convert back to dates
      const roundTripped: ConversationCommonItem = {
        ...parsed,
        createdAt: new Date(parsed.createdAt),
        updatedAt: new Date(parsed.updatedAt),
        deletedAt: parsed.deletedAt ? new Date(parsed.deletedAt) : undefined
      };

      const validation = this.validateConversationEquality(item, roundTripped);

      return {
        isValid: validation.isEqual,
        error: validation.isEqual ? undefined : validation.differences.join(', ')
      };
    } catch (error) {
      return {
        isValid: false,
        error: `JSON serialization failed: ${error}`
      };
    }
  }

  // ─── TASK VALIDATION METHODS ───

  /**
   * Validate that two tasks are equivalent (ignoring timestamps if specified)
   */
  static validateTaskEquality(
    expected: Task,
    actual: Task,
    options: {
      ignoreTimestamps?: boolean;
      ignoreFields?: string[];
    } = {}
  ): { isEqual: boolean; differences: string[] } {
    const differences: string[] = [];
    const { ignoreTimestamps = false, ignoreFields = [] } = options;

    // Fields to compare
    const fieldsToCompare = [
      'id',
      'name',
      'contentHash',
      'description',
      'status',
      'priority',
      'tags',
      'memoryId',
      'workspaceId',
      'data'
    ];

    if (!ignoreTimestamps) {
      fieldsToCompare.push('createdAt', 'updatedAt', 'deletedAt', 'completedAt', 'dueDate');
    }

    // Filter out ignored fields
    const finalFields = fieldsToCompare.filter((field) => !ignoreFields.includes(field));

    for (const field of finalFields) {
      const expectedValue = (expected as any)[field];
      const actualValue = (actual as any)[field];

      if (['createdAt', 'updatedAt', 'deletedAt', 'completedAt', 'dueDate'].includes(field)) {
        // Handle date comparison
        const expectedTime =
          expectedValue instanceof Date
            ? expectedValue.getTime()
            : expectedValue
              ? new Date(expectedValue).getTime()
              : null;
        const actualTime =
          actualValue instanceof Date
            ? actualValue.getTime()
            : actualValue
              ? new Date(actualValue).getTime()
              : null;

        if (expectedTime !== actualTime) {
          differences.push(`${field}: expected ${expectedValue}, got ${actualValue}`);
        }
      } else if (field === 'tags') {
        // Handle array comparison with deep equality to ignore property order
        if (!deepEqual(expectedValue || [], actualValue || [])) {
          differences.push(
            `${field}: expected ${JSON.stringify(expectedValue || [])}, got ${JSON.stringify(actualValue || [])}`
          );
        }
      } else if (field === 'data') {
        // Handle JSON data comparison with deep equality to ignore property order
        if (!deepEqual(expectedValue, actualValue)) {
          differences.push(
            `${field}: expected ${JSON.stringify(expectedValue)}, got ${JSON.stringify(actualValue)}`
          );
        }
      } else {
        // Handle simple value comparison
        if (expectedValue !== actualValue) {
          differences.push(`${field}: expected ${expectedValue}, got ${actualValue}`);
        }
      }
    }

    // Add debug logging for failed validations
    if (differences.length > 0) {
      console.log('Task validation failed with differences:', differences);
      console.log('Expected task:', JSON.stringify(expected, null, 2));
      console.log('Actual task:', JSON.stringify(actual, null, 2));
    }

    return {
      isEqual: differences.length === 0,
      differences
    };
  }

  /**
   * Validate that all tasks have unique IDs
   */
  static validateUniqueTaskIds(tasks: Task[]): {
    isUnique: boolean;
    duplicates: string[];
  } {
    const seen = new Set<string>();
    const duplicates: string[] = [];

    for (const task of tasks) {
      if (seen.has(task.id)) {
        duplicates.push(task.id);
      } else {
        seen.add(task.id);
      }
    }

    return {
      isUnique: duplicates.length === 0,
      duplicates
    };
  }

  /**
   * Validate that required task fields are present and not null/undefined
   */
  static validateRequiredTaskFields(
    task: Task,
    requiredFields: (keyof Task)[] = ['id', 'name', 'status', 'createdAt', 'updatedAt']
  ): { isValid: boolean; missingFields: string[] } {
    const missingFields: string[] = [];

    for (const field of requiredFields) {
      const value = (task as any)[field];
      if (value === null || value === undefined || value === '') {
        missingFields.push(field as string);
      }
    }

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  }

  /**
   * Validate that task data can be round-tripped through JSON serialization
   */
  static validateTaskJsonRoundTrip(task: Task): { isValid: boolean; error?: string } {
    try {
      // Convert dates to strings (simulating database storage)
      const serialized = {
        ...task,
        createdAt: task.createdAt.toISOString(),
        updatedAt: task.updatedAt.toISOString(),
        deletedAt: task.deletedAt?.toISOString(),
        completedAt: task.completedAt?.toISOString(),
        dueDate: task.dueDate?.toISOString()
      };

      const jsonString = JSON.stringify(serialized);
      const parsed = JSON.parse(jsonString);

      // Convert back to dates
      const roundTripped: Task = {
        ...parsed,
        createdAt: new Date(parsed.createdAt),
        updatedAt: new Date(parsed.updatedAt),
        deletedAt: parsed.deletedAt ? new Date(parsed.deletedAt) : undefined,
        completedAt: parsed.completedAt ? new Date(parsed.completedAt) : undefined,
        dueDate: parsed.dueDate ? new Date(parsed.dueDate) : undefined
      };

      const validation = this.validateTaskEquality(task, roundTripped);

      return {
        isValid: validation.isEqual,
        error: validation.isEqual ? undefined : validation.differences.join(', ')
      };
    } catch (error) {
      return {
        isValid: false,
        error: `JSON serialization failed: ${error}`
      };
    }
  }

  /**
   * Validate task deduplication based on contentHash
   */
  static validateTaskDeduplication(tasks: Task[]): {
    isValid: boolean;
    duplicateContentHashes: string[];
  } {
    const seenHashes = new Set<string>();
    const duplicateContentHashes: string[] = [];

    for (const task of tasks) {
      if (task.contentHash) {
        if (seenHashes.has(task.contentHash)) {
          duplicateContentHashes.push(task.contentHash);
        } else {
          seenHashes.add(task.contentHash);
        }
      }
    }

    return {
      isValid: duplicateContentHashes.length === 0,
      duplicateContentHashes
    };
  }

  // ─── MESSAGE VALIDATION METHODS ───

  /**
   * Validate that two messages are equivalent (ignoring timestamps if specified)
   */
  static validateMessageEquality(
    expected: ExchangeMessage,
    actual: ExchangeMessage,
    options: {
      ignoreTimestamps?: boolean;
      ignoreFields?: string[];
    } = {}
  ): { isEqual: boolean; differences: string[] } {
    const differences: string[] = [];
    const { ignoreTimestamps = false, ignoreFields = [] } = options;

    // Fields to compare
    const fieldsToCompare = [
      'id',
      'type',
      'subtype',
      'sender',
      'query',
      'data',
      'queryOptions',
      'sources',
      'transcriptId',
      'conversationId'
    ];

    if (!ignoreTimestamps) {
      fieldsToCompare.push('date', 'createdAt', 'updatedAt', 'deletedAt');
    }

    // Filter out ignored fields
    const finalFields = fieldsToCompare.filter((field) => !ignoreFields.includes(field));

    for (const field of finalFields) {
      const expectedValue = (expected as any)[field];
      const actualValue = (actual as any)[field];

      if (['date', 'createdAt', 'updatedAt', 'deletedAt'].includes(field)) {
        // Handle date comparison
        const expectedTime =
          expectedValue instanceof Date
            ? expectedValue.getTime()
            : expectedValue
              ? new Date(expectedValue).getTime()
              : null;
        const actualTime =
          actualValue instanceof Date
            ? actualValue.getTime()
            : actualValue
              ? new Date(actualValue).getTime()
              : null;

        if (expectedTime !== actualTime) {
          differences.push(`${field}: expected ${expectedValue}, got ${actualValue}`);
        }
      } else if (field === 'data' || field === 'queryOptions' || field === 'sources') {
        // Handle JSON data comparison with deep equality to ignore property order
        // Special normalization for sources field: treat [] and undefined as equivalent
        let normalizedExpected = expectedValue;
        let normalizedActual = actualValue;

        if (field === 'sources') {
          // Normalize sources: treat [] and undefined as equivalent
          normalizedExpected = normalizeSourcesField(expectedValue);
          normalizedActual = normalizeSourcesField(actualValue);
        }

        // Drop nullish keys before deep comparison to treat null/undefined equivalently
        normalizedExpected = ValidationUtils.dropNullishKeysDeep(normalizedExpected);
        normalizedActual = ValidationUtils.dropNullishKeysDeep(normalizedActual);

        const isEqual = deepEqual(normalizedExpected, normalizedActual);
        if (!isEqual) {
          console.log(`Field ${field} failed deep equality check:`);
          console.log(`Expected:`, expectedValue, JSON.stringify(expectedValue));
          console.log(`Actual:`, actualValue, JSON.stringify(actualValue));
          differences.push(
            `${field}: expected ${JSON.stringify(expectedValue)}, got ${JSON.stringify(actualValue)}`
          );
        }
      } else {
        // Handle simple value comparison
        if (expectedValue !== actualValue) {
          differences.push(`${field}: expected ${expectedValue}, got ${actualValue}`);
        }
      }
    }

    // Add debug logging for failed validations
    if (differences.length > 0) {
      console.log('Message validation failed with differences:', differences);
      console.log('Expected message:', JSON.stringify(expected, null, 2));
      console.log('Actual message:', JSON.stringify(actual, null, 2));
    }

    return {
      isEqual: differences.length === 0,
      differences
    };
  }

  /**
   * Validate that all messages have unique IDs
   */
  static validateUniqueMessageIds(messages: ExchangeMessage[]): {
    isUnique: boolean;
    duplicates: string[];
  } {
    const seen = new Set<string>();
    const duplicates: string[] = [];

    for (const message of messages) {
      if (seen.has(message.id)) {
        duplicates.push(message.id);
      } else {
        seen.add(message.id);
      }
    }

    return {
      isUnique: duplicates.length === 0,
      duplicates
    };
  }

  /**
   * Validate that required message fields are present and not null/undefined
   */
  static validateRequiredMessageFields(
    message: ExchangeMessage,
    requiredFields: string[] = ['id', 'type', 'sender', 'date', 'createdAt', 'updatedAt']
  ): { isValid: boolean; missingFields: string[] } {
    const missingFields: string[] = [];

    for (const field of requiredFields) {
      const value = (message as any)[field];
      if (value === null || value === undefined || value === '') {
        missingFields.push(field);
      }
    }

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  }

  /**
   * Validate that message data can be round-tripped through JSON serialization
   */
  static validateMessageJsonRoundTrip(message: ExchangeMessage): {
    isValid: boolean;
    error?: string;
  } {
    try {
      // Convert dates to strings (simulating database storage)
      const serialized = {
        ...message,
        date: message.date.toISOString(),
        createdAt: message.createdAt.toISOString(),
        updatedAt: message.updatedAt.toISOString(),
        deletedAt: message.deletedAt?.toISOString()
      };

      const jsonString = JSON.stringify(serialized);
      const parsed = JSON.parse(jsonString);

      // Convert back to dates
      const roundTripped: ExchangeMessage = {
        ...parsed,
        date: new Date(parsed.date),
        createdAt: new Date(parsed.createdAt),
        updatedAt: new Date(parsed.updatedAt),
        deletedAt: parsed.deletedAt ? new Date(parsed.deletedAt) : undefined
      };

      // Handle nested Date objects in sources array
      if (roundTripped.sources && Array.isArray(roundTripped.sources)) {
        roundTripped.sources = roundTripped.sources.map((source: any) => {
          if (source && typeof source === 'object' && source.date) {
            return {
              ...source,
              date: new Date(source.date)
            };
          }
          return source;
        });
      }

      const validation = this.validateMessageEquality(message, roundTripped);

      return {
        isValid: validation.isEqual,
        error: validation.isEqual ? undefined : validation.differences.join(', ')
      };
    } catch (error) {
      return {
        isValid: false,
        error: `JSON serialization failed: ${error}`
      };
    }
  }

  /**
   * Validate message-conversation relationships
   */
  static validateMessageConversationRelationship(
    messages: ExchangeMessage[],
    conversations: ConversationCommonItem[]
  ): {
    isValid: boolean;
    orphanedMessages: string[];
    missingConversations: string[];
  } {
    const conversationIds = new Set(conversations.map((c) => c.id));
    const orphanedMessages: string[] = [];
    const missingConversations = new Set<string>();

    for (const message of messages) {
      if (message.conversationId) {
        if (!conversationIds.has(message.conversationId)) {
          orphanedMessages.push(message.id);
          missingConversations.add(message.conversationId);
        }
      }
    }

    return {
      isValid: orphanedMessages.length === 0,
      orphanedMessages,
      missingConversations: Array.from(missingConversations)
    };
  }

  // ─── TRANSCRIPT VALIDATION ───

  /**
   * Validate that two transcripts are equal
   */
  static validateTranscriptEquality(
    transcript1: TranscriptFull,
    transcript2: TranscriptFull,
    doLog: boolean = false
  ): { isEqual: boolean; differences: string[] } {
    const differences: string[] = [];
    const log = doLog ? console.log : () => {};

    // Basic fields
    const basicFields: (keyof TranscriptFull)[] = [
      'id',
      'name',
      'editedName',
      'status',
      'eventId',
      'fileExists',
      'isDeleted',
      'notionMeetingId',
      'userNotes',
      'workspaceId',
      'userId'
    ];

    for (const field of basicFields) {
      const val1 = transcript1[field];
      const val2 = transcript2[field];

      if (!deepEqual(val1, val2)) {
        differences.push(
          `Field '${field}' differs: ${transcript1[field]} vs ${transcript2[field]}`
        );
      }
    }

    // Duration field - floor to integer for comparison (Prisma truncates floats to Int)
    // Omit sub-second durations (< 1 second is meaningless)
    const dur1 = transcript1.duration;
    const dur2 = transcript2.duration;
    const normDur1 =
      dur1 !== undefined && dur1 !== null && dur1 >= 1 ? Math.floor(dur1) : undefined;
    const normDur2 =
      dur2 !== undefined && dur2 !== null && dur2 >= 1 ? Math.floor(dur2) : undefined;
    if (normDur1 !== normDur2) {
      differences.push(
        `Field 'duration' differs: ${transcript1.duration} vs ${transcript2.duration} (normalized: ${normDur1} vs ${normDur2})`
      );
    }

    // Date fields - normalize to timestamps
    const dateFields: (keyof TranscriptFull)[] = ['date', 'createdAt', 'updatedAt', 'deletedAt'];
    for (const field of dateFields) {
      const val1 = transcript1[field];
      const val2 = transcript2[field];

      if (val1 && val2) {
        const time1 =
          val1 instanceof Date ? val1.getTime() : new Date(val1 as string | number).getTime();
        const time2 =
          val2 instanceof Date ? val2.getTime() : new Date(val2 as string | number).getTime();

        if (Math.abs(time1 - time2) > 1000) {
          // Allow 1 second difference for timestamp precision
          differences.push(
            `Date field '${field}' differs by more than 1 second: ${val1} vs ${val2}`
          );
        }
      } else if (val1 !== val2) {
        differences.push(`Date field '${field}' differs: ${val1} vs ${val2}`);
      }
    }

    // JSON fields - compare deep structure
    const jsonFields: (keyof TranscriptFull)[] = [
      'file',
      'data',
      'rawOutput',
      'editedOutput',
      'output',
      'meeting'
    ];

    let hasMeetingDifferences = false;

    for (const field of jsonFields) {
      let val1 = transcript1[field];
      let val2 = transcript2[field];

      // Normalize empty objects to undefined for comparison
      if (
        val1 &&
        typeof val1 === 'object' &&
        !Array.isArray(val1) &&
        Object.keys(val1).length === 0
      ) {
        val1 = undefined;
      }
      if (
        val2 &&
        typeof val2 === 'object' &&
        !Array.isArray(val2) &&
        Object.keys(val2).length === 0
      ) {
        val2 = undefined;
      }

      let shouldAddDifference = !deepEqual(val1, val2);

      // Special handling for meeting field differences
      if (field === 'meeting' && shouldAddDifference) {
        // Custom comparison for meeting field that treats missing keys and undefined values as the same
        const meeting1 = transcript1.meeting;
        const meeting2 = transcript2.meeting;

        let meetingIsEqual = true;

        if (meeting1 && meeting2 && typeof meeting1 === 'object' && typeof meeting2 === 'object') {
          // Get all unique keys from both objects
          const allKeys = new Set([...Object.keys(meeting1), ...Object.keys(meeting2)]);

          for (const key of allKeys) {
            const val1 = (meeting1 as Record<string, any>)[key];
            const val2 = (meeting2 as Record<string, any>)[key];

            // Treat missing key and undefined as the same
            const effectiveVal1 = key in meeting1 ? val1 : undefined;
            const effectiveVal2 = key in meeting2 ? val2 : undefined;

            if (!deepEqual(effectiveVal1, effectiveVal2)) {
              meetingIsEqual = false;
              break;
            }
          }
        } else {
          // Fall back to regular deep comparison for non-objects
          meetingIsEqual = deepEqual(meeting1, meeting2);
        }

        if (!meetingIsEqual) {
          hasMeetingDifferences = true;
          log(`[DEBUG] Meeting field differs between transcripts:`);
          log(`  Transcript1 ID: ${transcript1.id}`);
          log(`  Transcript2 ID: ${transcript2.id}`);

          log(`  Meeting1 (${typeof meeting1}):`);
          if (meeting1 && typeof meeting1 === 'object') {
            for (const [key, value] of Object.entries(meeting1)) {
              log(`    ${key}: ${typeof value} = ${JSON.stringify(value)}`);
            }
          } else {
            log(`    ${JSON.stringify(meeting1)}`);
          }

          log(`  Meeting2 (${typeof meeting2}):`);
          if (meeting2 && typeof meeting2 === 'object') {
            for (const [key, value] of Object.entries(meeting2)) {
              log(`    ${key}: ${typeof value} = ${JSON.stringify(value)}`);
            }
          } else {
            log(`    ${JSON.stringify(meeting2)}`);
          }
        } else {
          // Meeting fields are actually equal, don't add difference
          shouldAddDifference = false;
        }
      }

      if (shouldAddDifference) {
        differences.push(
          `JSON field '${field}' differs: ${JSON.stringify(transcript1[field])} vs ${JSON.stringify(transcript2[field])}`
        );
      }
    }

    if (differences.length && !doLog && hasMeetingDifferences) {
      // run again with logging
      ValidationUtils.validateTranscriptEquality(transcript1, transcript2, true);
    }

    return {
      isEqual: differences.length === 0,
      differences
    };
  }

  /**
   * Validate that all transcript IDs in an array are unique
   */
  static validateUniqueTranscriptIds(transcripts: Transcript[]): {
    isUnique: boolean;
    duplicates: string[];
  } {
    const seen = new Set<string>();
    const duplicates: string[] = [];

    for (const transcript of transcripts) {
      if (seen.has(transcript.id)) {
        duplicates.push(transcript.id);
      } else {
        seen.add(transcript.id);
      }
    }

    return {
      isUnique: duplicates.length === 0,
      duplicates
    };
  }

  /**
   * Validate that transcript has all required fields
   */
  static validateRequiredTranscriptFields(transcript: TranscriptFull): {
    isValid: boolean;
    missingFields: string[];
  } {
    const requiredFields: (keyof Transcript)[] = [
      'id',
      'name',
      'status',
      'fileExists',
      'isDeleted',
      'userId',
      'createdAt',
      'updatedAt',
      'date'
    ];

    const missingFields: string[] = [];

    for (const field of requiredFields) {
      if (transcript[field] === undefined || transcript[field] === null) {
        missingFields.push(String(field));
      }
    }

    // Special validation for JSON fields that shouldn't be null
    if (!transcript.rawOutput || !Array.isArray(transcript.rawOutput)) {
      missingFields.push('rawOutput (must be array)');
    }

    if (!transcript.editedOutput || !Array.isArray(transcript.editedOutput)) {
      missingFields.push('editedOutput (must be array)');
    }

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  }

  /**
   * Validate JSON round-trip for transcript serialization
   */
  static validateTranscriptJsonRoundTrip(transcript: Transcript): {
    isValid: boolean;
    error?: string;
  } {
    try {
      const serialized = JSON.stringify(transcript);
      const deserialized = JSON.parse(serialized);

      // Check if all important fields survived the round-trip
      const important = ['id', 'name', 'status', 'rawOutput', 'editedOutput', 'data', 'file'];
      for (const field of important) {
        if (!deepEqual((transcript as any)[field], deserialized[field])) {
          return {
            isValid: false,
            error: `Field '${field}' changed during JSON round-trip`
          };
        }
      }

      return { isValid: true };
    } catch (error) {
      return {
        isValid: false,
        error: `JSON serialization failed: ${(error as Error).message}`
      };
    }
  }
}
